import '../fonts/fonts.css'
import '../i18n'

import { css, Global, SerializedStyles } from '@emotion/core'
import { useLocation } from '@reach/router'
import React from 'react'

import { breakpoints, emptySpaceWidthBreakPoint } from '../constants/breakPoints'
import { isMobile } from '../helpers/common'
import { useSiteMetadata } from '../hooks/useSiteMetadata'
import Banner from './Banner'
import { Link } from './Link'
import { ThirdParty } from './ThirdParty'
import { identifyUser, useGoogleAnalyticsClientId, useUtmTracking } from './useTracking'

const defaultTopBannerMessage = (
  <div className="center">
    Exciting news: airfocus is joining forces with Lucid Software!{' '}
    <Link
      target="_blank"
      className="white-text opacity-on-hover"
      href="https://airfocus.com/blog/lucid-airfocus-announcement/"
      css={css`
        text-decoration: underline;
        font-weight: 600;
      `}
    >
      Read all about it
    </Link>
    .
  </div>
)

type backgroundProps =
  | { bgColor: string; whiteBG?: undefined; blackBG?: undefined }
  | { bgColor?: undefined; whiteBG?: boolean; blackBG?: undefined }
  | { bgColor?: undefined; whiteBG?: undefined; blackBG?: boolean }
type bannerProps =
  | { withTopBanner?: boolean; topBannerMessage?: undefined; showBannerOnSpecificPagesOnly?: boolean }
  | { withTopBanner: boolean; topBannerMessage?: React.ReactNode; showBannerOnSpecificPagesOnly?: boolean }

type LayoutProps = backgroundProps &
  bannerProps & {
    withMarketing?: boolean
    children?: React.ReactNode
    cssStyles?: SerializedStyles
  }

const Layout: React.FunctionComponent<LayoutProps> = ({
  children,
  bgColor,
  cssStyles,
  whiteBG = false,
  blackBG = false,
  withMarketing = true,
  withTopBanner = true,
  topBannerMessage = defaultTopBannerMessage,
  // show <Banner /> only on homepage & product LPs
  showBannerOnSpecificPagesOnly = false,
}) => {
  const { marketing } = useSiteMetadata()
  const [anonymousId, utm] = useUtmTracking()
  const googleAnalyticsClientId = useGoogleAnalyticsClientId()

  // if (showBannerOnSpecificPagesOnly): show <Banner /> only on stated slugs below
  const location = useLocation()
  const isSpecificPage =
    location.pathname === '/' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname.startsWith('/resources/events/')

  React.useEffect(() => {
    window.setTimeout(() => {
      try {
        if (withMarketing && anonymousId) {
          identifyUser(anonymousId, utm, googleAnalyticsClientId)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }, 0)
  }, [withMarketing, anonymousId, utm, googleAnalyticsClientId])

  return (
    <React.Fragment>
      <ThirdParty marketingMetaData={marketing} withMarketing={withMarketing} anonymousId={anonymousId} />
      <Global styles={[globalStyle(blackBG, whiteBG, bgColor), cssStyles]} />
      <div css={containerStyle}>
        {withTopBanner &&
          !isMobile() &&
          (showBannerOnSpecificPagesOnly ? (
            isSpecificPage && <Banner message={topBannerMessage} id="research-banner" />
          ) : (
            <Banner message={topBannerMessage} id="research-banner" />
          ))}
        {children}
      </div>
    </React.Fragment>
  )
}

export default Layout

export const baseFontSize = 16

const globalStyle = (blackBG: boolean, whiteBG: boolean, bgColor?: string) => css`
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  :root {
    --emptySpaceWidth: calc((100vw - 1136px) / 2);
    --blue-gradient: linear-gradient(to bottom, #359feb, #0e70e8);
    --button-hover-gradient: linear-gradient(to bottom, #35b8eb, #0e70e8);
  }

  html {
    font-size: 16px;
  }

  html,
  body {
    scroll-behavior: smooth;
    margin: 0;
    padding: 0;
  }

  body {
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;

    background-color: ${bgColor ? bgColor : blackBG ? '#071322' : whiteBG ? '#ffffff' : '#f9fbfc'};

    font-family:
      'Inter',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Oxygen-Sans,
      Ubuntu,
      Cantarell,
      'Helvetica Neue',
      sans-serif;

    font-size: ${baseFontSize}px;

    color: ${blackBG ? '#fff' : '#0a1c33'};
    line-height: 1.5;

    * {
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      -webkit-tap-highlight-color: transparent;
    }

    h1,
    h2,
    h3,
    h4,
    .cta-sidebar-title {
      margin: 0;
      font-weight: bold !important;
      font-family:
        'Eina 01',
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        Oxygen-Sans,
        Ubuntu,
        Cantarell,
        'Helvetica Neue',
        sans-serif;
    }

    .heading1 {
      font-size: ${(64 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(64 / baseFontSize).toFixed(3)}rem !important;
      letter-spacing: -1px;
      font-weight: 700;
      &.blue-mesh-text {
        padding-bottom: 0.47rem;
      }
      &.small {
        font-size: ${(48 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(50 / baseFontSize).toFixed(3)}rem !important;
      }
    }

    .heading2 {
      font-size: ${(36 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(40 / baseFontSize).toFixed(3)}rem !important;
      font-weight: bold !important;
      margin: 8rem 0 4rem;

      &.black-text,
      &.white-text {
        margin-bottom: 1.5rem;
      }

      &.blue-mesh-text {
        padding-bottom: 0.38rem;
      }
    }

    .heading3 {
      font-size: ${(24 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(28 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 700;
    }

    .heading4 {
      font-size: ${(18 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(26 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 700;
    }

    .title {
      font-size: ${(21 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(27 / baseFontSize).toFixed(3)}rem !important;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    .subtitle {
      font-family:
        'Inter',
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        Oxygen-Sans,
        Ubuntu,
        Cantarell,
        'Helvetica Neue',
        sans-serif;
      font-size: ${(18 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 400;
      line-height: ${(26 / baseFontSize).toFixed(3)}rem !important;
      margin: 1.5rem 0 0.5rem;

      &.long-copy {
        line-height: ${(30 / baseFontSize).toFixed(3)}rem !important;
      }
    }

    .body-1 {
      font-size: 1rem;
      font-weight: 400;
      margin: 1rem 0 0.5rem;
    }

    .strong-body-1 {
      font-size: 1rem;
      font-weight: 600;
      line-height: 1.5rem;
    }

    .body-2 {
      font-size: ${(14 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 400;
    }

    .cta-sidebar-title {
      font-size: ${(18 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(22 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 700;
    }

    .strong-body-2 {
      font-size: ${(14 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 600;
    }

    .footnote {
      font-size: ${(12 / baseFontSize).toFixed(3)}rem !important;
      color: #88929e !important;
    }

    .quote {
      font-size: ${(20 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(28 / baseFontSize).toFixed(3)}rem !important;
      margin: ${(12 / baseFontSize).toFixed(3)}rem 0 ${(16 / baseFontSize).toFixed(3)}rem !important ;
      font-weight: 600;
      letter-spacing: -1px;
      .paragraph {
        font-size: ${(20 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(28 / baseFontSize).toFixed(3)}rem !important;
        margin: ${(12 / baseFontSize).toFixed(3)}rem 0 ${(16 / baseFontSize).toFixed(3)}rem !important ;
        font-weight: 600;
        letter-spacing: -1px;
      }
    }

    .categories {
      ${blackBG && 'color: #fff;'};
      font-size: ${(16 / baseFontSize).toFixed(3)}rem !important;
      line-height: ${(18 / baseFontSize).toFixed(3)}rem !important;
      font-weight: 600;
    }

    @media screen and (max-width: ${breakpoints.large}px) {
      .heading1 {
        font-size: ${(60 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(60 / baseFontSize).toFixed(3)}rem !important;
      }

      .subtitle {
        font-size: ${(18 / baseFontSize).toFixed(3)}rem !important;
        font-weight: 400;
        line-height: ${(24 / baseFontSize).toFixed(3)}rem !important;
        margin: 1.5rem 0 0.5rem;

        &.long-copy {
          line-height: ${(30 / baseFontSize).toFixed(3)}rem !important;
        }
      }
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      .heading1 {
        font-size: ${(48 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(50 / baseFontSize).toFixed(3)}rem !important;
      }

      .heading2 {
        font-size: ${(36 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(36 / baseFontSize).toFixed(3)}rem !important;
        font-weight: bold !important;
        margin: 4rem 0 2rem;

        &.black-text,
        &.white-text {
          margin-bottom: 1rem;
        }

        &.blue-mesh-text {
          padding-bottom: 0.25rem;
        }
      }

      .subtitle {
        font-size: ${(16 / baseFontSize).toFixed(3)}rem !important;
        line-height: ${(22 / baseFontSize).toFixed(3)}rem !important;

        &.long-copy {
          line-height: ${(30 / baseFontSize).toFixed(3)}rem !important;
        }
      }

      @media screen and (max-width: ${breakpoints.small}px) {
        * {
          text-align: left;
        }

        .heading2 {
          text-align: left !important;
        }

        .heading2.center-on-small {
          text-align: center !important;
        }
      }
    }

    .heading-subtitle-link-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 4rem;

      @media screen and (max-width: ${breakpoints.small}px) {
        align-items: flex-start;
        margin-bottom: 2rem;

        .body-1,
        .subtitle {
          text-align: left !important;
        }
      }
    }

    .beta {
      color: #efeff0;
      display: inline-flex;
      font-size: 0.65rem;
      font-weight: 500;
      border-radius: 0.25rem;
      background-color: #44505f;
    }

    .soon {
      color: #318eff;
      display: inline-flex;
      font-size: 0.65rem;
      font-weight: 500;
      border-radius: 0.25rem;
      background-color: rgb(236, 243, 251);
    }

    .section {
      color: #909090;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
    }

    .blue-gradient-text {
      background: var(--blue-gradient);
      background-clip: text;
      -webkit-text-fill-color: transparent;
      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
    }

    .blue-mesh-text {
      background:
        url('/images/background-mesh-gradient.jpg') center no-repeat,
        linear-gradient(135deg, #54d5eb, #266eea);
      background-size: cover;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      -webkit-background-clip: text;
      -moz-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-text-fill-color: transparent;
      animation: meshanimate 2500ms ease-in-out infinite;
      animation-direction: alternate;
    }

    .purple-bg {
      background-color: #eae0ff;
    }

    .blue-gradient-bg,
    .green-gradient-bg,
    .peach-gradient-bg,
    .purple-gradient-bg {
      background-size: cover;
      background-repeat: no-repeat;
    }
    .blue-gradient-bg {
      background-image: url('/images/blue-gradient-bg.webp');
    }
    .green-gradient-bg {
      background-image: url('/images/green-gradient-bg.webp');
    }
    .peach-gradient-bg {
      background-image: url('/images/peach-gradient-bg.webp');
    }
    .purple-gradient-bg {
      background-image: url('/images/purple-gradient-bg.webp');
    }

    .blue-bg-block {
      position: relative;
      background-color: rgba(173, 230, 255, 0.22);
      margin: 8rem 0;
      padding: 6rem 0;

      @media screen and (max-width: ${breakpoints.small}px) {
        padding: 4rem 0;
      }
    }

    .gray-bg-block {
      border-radius: 0.5rem;
      background: ${blackBG ? 'rgba(255,255,255,0.1)' : '#fafbfc'};
      border: 1px solid ${blackBG ? 'rgba(255,255,255,0.1)' : '#efeff0'};
    }

    .gray-bordered-block {
      border: 1px #efeff0 solid;
      border-radius: 0.5rem;
      overflow: hidden;
      box-shadow: rgb(0 0 0 / 8%) 0px 0.5rem 2rem;
      // temporary fix for Safari (overflow: hidden is disregarded otherwise)
      isolation: isolate;
    }

    .with-backdrop-filter {
      ${blackBG
        ? `
          background: linear-gradient(rgba(7, 19, 34, 0.69) 0%, #071322 100%);
          backdrop-filter: blur(15px);
      `
        : `
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(20px);
      `}
    }

    .with-box-shadow {
      box-shadow: rgb(0 0 0 / 8%) 0 0.5rem 2rem;
    }

    .with-blue-box-shadow {
      box-shadow: 2px 2px 20px rgba(69, 118, 255, 0.3);
    }

    .fade-in-left {
      animation: fadeInLeft 400ms;

      @keyframes fadeInLeft {
        0% {
          opacity: 0;
          transform: translateX(-20px);
        }
        100% {
          opacity: 1;
          transform: translateX(0);
        }
      }
    }

    @keyframes meshanimate {
      0% {
        filter: hue-rotate(10deg);
      }
      100% {
        filter: hue-rotate(30deg);
      }
    }

    .black-text {
      color: #0a1c33;
    }

    .blue-text {
      color: #318eff;
    }

    .gray-text {
      color: #909090;
    }

    .white-text {
      color: #fff !important;
    }

    .center {
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }

    .left {
      text-align: left;
    }

    .right {
      text-align: right;
    }

    .bold {
      font-weight: bold !important;
    }

    .italic {
      font-style: italic !important;
    }

    .anchor-link {
      padding-top: 8.7rem !important;
      margin-top: -8.7rem !important;

      @media screen and (max-width: ${breakpoints.medium}px) {
        padding-top: 6.6rem !important;
        margin-top: -6.6rem !important;
      }
    }

    .anchor-link-with-secondary-navigation {
      padding-top: 13.3rem !important;
      margin-top: -13.3rem !important;

      @media screen and (max-width: ${breakpoints.medium}px) {
        padding-top: 11rem !important;
        margin-top: -11rem !important;
      }
    }

    h1,
    h2,
    h3,
    h4,
    p {
      &:first-of-type {
        margin-top: 0;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }

    hr {
      border: 0;
      border-bottom: 1px solid silver;
    }

    a {
      color: #318eff;
      text-decoration: none;
      cursor: pointer !important;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;

      li {
        margin-bottom: 0.25rem;
      }

      @media screen and (max-width: ${breakpoints.small}px) {
        text-align: left;
      }
    }
  }

  .opacity-on-hover {
    transition: all 0.125s ease;
    &:hover {
      opacity: 0.65 !important;
      transition: none !important;
    }
  }

  .translate-and-shadow-on-hover {
    transition: all 0.3s ease-in-out;
    &:hover {
      transform: translateY(-8px);
      box-shadow: rgba(0, 0, 0, 0.05) 0 1rem 1rem 0;
    }
  }

  .scale-on-hover {
    transition: all 0.2s ease;
    &:hover {
      transform: scale(1.03);
    }
  }

  .full-screen-relative,
  .full-screen-fixed,
  .full-screen-sticky {
    padding-left: var(--emptySpaceWidth) !important;
    padding-right: var(--emptySpaceWidth) !important;

    @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
      padding-left: 2rem !important;
      padding-right: 2rem !important;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      padding-left: 1.5rem !important;
      padding-right: 1.5rem !important;
    }
  }

  .full-screen-sticky {
    margin-left: calc(var(--emptySpaceWidth) * -1) !important;
    width: 100% !important;

    @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
      margin-left: -2rem !important;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      margin-left: -1.5rem !important;
    }
  }

  .full-screen-relative {
    left: calc(var(--emptySpaceWidth) * -1) !important;
    width: 100% !important;

    @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
      left: -2rem !important;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      left: -1.5rem !important;
    }
  }

  .show-only-on-xlarge {
    @media screen and (max-width: ${breakpoints.xlarge}px) {
      display: none !important;
    }
  }

  .hide-on-desktop {
    @media screen and (min-width: ${breakpoints.large + 1}px) {
      display: none !important;
    }
  }

  .hide-on-large-desktop {
    @media screen and (min-width: ${breakpoints.medium + 1}px) {
      display: none !important;
    }
  }

  .hide-on-large {
    @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${breakpoints.large}px) {
      display: none !important;
    }
  }

  .hide-on-medium-large-desktop {
    @media screen and (min-width: ${breakpoints.small + 1}px) {
      display: none !important;
    }
  }
  .hide-on-medium-large {
    @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.large}px) {
      display: none !important;
    }
  }

  .hide-on-medium {
    @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.medium}px) {
      display: none !important;
    }
  }

  .hide-on-small-medium-large {
    @media screen and (max-width: ${breakpoints.large}px) {
      display: none !important;
    }
  }

  .hide-on-small-medium {
    @media screen and (max-width: ${breakpoints.medium}px) {
      display: none !important;
    }
  }

  .hide-on-small {
    @media screen and (max-width: ${breakpoints.small}px) {
      display: none !important;
    }
  }

  .intercom-namespace .intercom-1d87wj3 {
    height: calc(100% - 180px) !important;
  }

  .no-margin {
    margin: 0 !important;
  }

  .left-on-small-medium {
    @media screen and (max-width: ${breakpoints.medium}px) {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }

  .left-on-small {
    @media screen and (max-width: ${breakpoints.small}px) {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }

  .half-width {
    max-width: 50% !important;
  }

  .third-width {
    max-width: 66% !important;
  }

  .white-icon {
    path {
      fill: #fff;
    }
  }

  .gatsby-image-wrapper {
    position: relative;
    z-index: 0;
  }
`

const containerStyle = css`
  max-width: 1136px;
  padding: 0 var(--emptySpaceWidth);

  @media screen and (max-width: ${emptySpaceWidthBreakPoint}px) {
    padding: 0 2rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding: 0 1.5rem;
    max-width: 100%;
  }
`
